import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Card, Select, InputNumber, Radio, message, Row, Col } from 'antd';
import { SaveOutlined, RollbackOutlined } from '@ant-design/icons';
import { members, stores, staffs, categoryOptions } from '../mockData';

const { Option } = Select;

const MemberForm = ({ user }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const isEditing = Boolean(id);
  
  // 检查当前用户是否有权限
  const checkPermission = () => {
    if (user.role === 'staff') {
      message.error('您没有添加/编辑会员的权限');
      navigate('/members');
      return false;
    }
    return true;
  };
  
  useEffect(() => {
    // 检查权限
    if (!checkPermission()) return;
    
    // 编辑模式下，填充表单数据
    if (isEditing) {
      const memberToEdit = members.find(m => m.id === parseInt(id));
      
      if (memberToEdit) {
        form.setFieldsValue(memberToEdit);
      } else {
        message.error('未找到该会员');
        navigate('/members');
      }
    } else {
      // 新增模式下，设置默认值
      form.setFieldsValue({
        gender: '男',
        category: 'B',
        points: 0,
        storeId: user.storeId,
      });
    }
  }, [id, isEditing, form, navigate]);

  // 获取当前用户可见的门店列表
  const getVisibleStores = () => {
    if (user.role === 'boss') {
      return stores;
    } else {
      return stores.filter(store => store.id === user.storeId);
    }
  };

  // 根据选择的门店ID获取该门店的员工列表
  const getStaffsByStore = (storeId) => {
    return staffs.filter(staff => staff.storeId === storeId && staff.role === 'staff');
  };

  // 当门店选择变化时，重置员工选择
  const handleStoreChange = (storeId) => {
    form.setFieldValue('staffId', undefined);
  };

  // 提交表单
  const handleSubmit = (values) => {
    console.log('表单提交数据:', values);
    message.success(isEditing ? '会员信息修改成功' : '会员添加成功');
    navigate('/members');
  };

  return (
    <Card 
      title={isEditing ? "编辑会员" : "添加会员"}
      extra={
        <Button 
          icon={<RollbackOutlined />} 
          onClick={() => navigate('/members')}
        >
          返回列表
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ gender: '男', category: 'B', points: 0 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="姓名"
              name="name"
              rules={[{ required: true, message: '请输入会员姓名' }]}
            >
              <Input placeholder="请输入会员姓名" />
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item
              label="性别"
              name="gender"
              rules={[{ required: true, message: '请选择性别' }]}
            >
              <Radio.Group>
                <Radio value="男">男</Radio>
                <Radio value="女">女</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="年龄"
              name="age"
              rules={[{ required: true, message: '请输入年龄' }]}
            >
              <InputNumber min={1} max={120} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item
              label="手机号"
              name="phone"
              rules={[
                { required: true, message: '请输入手机号' },
                { 
                  pattern: /^1[3-9]\d{9}$/, 
                  message: '请输入正确的手机号码格式' 
                }
              ]}
            >
              <Input placeholder="请输入手机号" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="地址"
          name="address"
          rules={[{ required: true, message: '请输入地址' }]}
        >
          <Input placeholder="请输入详细地址" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="会员等级"
              name="category"
              rules={[{ required: true, message: '请选择会员等级' }]}
            >
              <Select placeholder="请选择会员等级">
                {categoryOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item
              label="积分"
              name="points"
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item
              label="关联配偶ID"
              name="coupleId"
              tooltip="如有配偶，请输入配偶的会员ID，将作为夫妻关联"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="选填" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="所属门店"
              name="storeId"
              rules={[{ required: true, message: '请选择所属门店' }]}
            >
              <Select 
                placeholder="请选择所属门店" 
                onChange={handleStoreChange}
                disabled={user.role === 'manager'}
              >
                {getVisibleStores().map(store => (
                  <Option key={store.id} value={store.id}>
                    {store.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item
              label="服务顾问"
              name="staffId"
              rules={[{ required: true, message: '请选择服务顾问' }]}
              dependencies={['storeId']}
            >
              <Select 
                placeholder="请选择服务顾问"
              >
                {form.getFieldValue('storeId') && getStaffsByStore(form.getFieldValue('storeId')).map(staff => (
                  <Option key={staff.id} value={staff.id}>
                    {staff.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            icon={<SaveOutlined />}
            style={{ marginRight: 8 }}
          >
            保存
          </Button>
          <Button 
            onClick={() => navigate('/members')}
            icon={<RollbackOutlined />}
          >
            取消
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default MemberForm; 